import { Link } from '@tanstack/react-router';
import { CheckCircle2, ChevronRight, Circle } from 'lucide-react';
import { useState } from 'react';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import { FixedCostDialog } from './FixedCostDialog';

type Summary = RouterOutputs['period']['summary'];
type FixedCostItem = Summary['fixedCosts']['items'][number];

/** Every bill and loan this period: paid ones ticked, open ones adjustable. */
export function FixedCostsPanel({ summary }: { summary: Summary }) {
  const { items, unpaid } = summary.fixedCosts;
  const { year, month } = summary.period;
  const [editing, setEditing] = useState<FixedCostItem | null>(null);
  const paidCount = items.filter((f) => f.isPaid).length;

  return (
    <Panel
      title="Fasta kostnader"
      description={
        items.length > 0 ? `${paidCount} av ${items.length} betalda` : 'Räkningar och lån'
      }
      action={
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Link
            to="/bills"
            aria-label="Hantera räkningar"
            className="font-medium text-primary hover:underline"
          >
            Räkningar
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/loans"
            aria-label="Hantera lån"
            className="font-medium text-primary hover:underline"
          >
            Lån
          </Link>
        </span>
      }
    >
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Inga räkningar eller lån än. De dyker upp här när de hittas bland era köp, eller när ni
          lägger till dem.
        </p>
      ) : (
        <>
          <ul className="-mx-2">
            {items.map((item) => (
              <li key={`${item.kind}-${item.id}`}>
                {item.isPaid ? (
                  <div className="flex items-center gap-3 px-2 py-2.5 text-sm">
                    <CheckCircle2 className="size-4 shrink-0 text-positive" aria-label="Betald" />
                    <ItemName item={item} muted />
                    <span className="tabular-nums text-muted-foreground">{kr(item.paid)}</span>
                    <span className="size-4 shrink-0" aria-hidden />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing(item)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                    aria-label={`${item.name}, ${kr(item.expected)}, inte betald. Ändra belopp`}
                  >
                    <Circle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <ItemName item={item} />
                    <span className="tabular-nums">{kr(item.expected)}</span>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-between border-t border-border pt-3 text-sm">
            <span className="text-muted-foreground">Kvar att betala</span>
            <span className="font-semibold tabular-nums">{kr(unpaid)}</span>
          </div>
        </>
      )}

      {editing && (
        <FixedCostDialog
          item={editing}
          year={year}
          month={month}
          onClose={() => setEditing(null)}
        />
      )}
    </Panel>
  );
}

function ItemName({ item, muted }: { item: FixedCostItem; muted?: boolean }) {
  return (
    <span className="flex min-w-0 flex-1 items-baseline gap-2">
      <span className={muted ? 'truncate text-muted-foreground' : 'truncate'}>{item.name}</span>
      {item.kind === 'loan' && <span className="shrink-0 text-xs text-muted-foreground">lån</span>}
    </span>
  );
}
